/**
 * ImpersonateModal — okno „Zaloguj się do tenanta" w panelu ITE (Etap 1 „impersonacja v2").
 *
 * Wdrożeniowiec wybiera z listy, NA CZYJE konto wchodzi, i podaje obowiązkowy powód wejścia
 * (min. 5 znaków). Lista pochodzi z `GET /admin/tenants/:id/impersonation-targets` (tylko aktywni,
 * ludzcy, nieusunięci); domyślnie zaznaczony jest pierwszy superużytkownik (jak dotąd).
 *
 * Start sesji: `POST /admin/tenants/:id/impersonate { targetUserId, reason }` → jednorazowy klucz
 * w fragmencie adresu (`#__impSessionKey=`), nowa karta z panelem firmy. Powód i wybór trafiają
 * do śladu po stronie serwera.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { RadioGroup } from '@/components/RadioGroup';
import { SearchInput } from '@/components/SearchInput';
import { Spinner } from '@/components/Spinner';
import { Textarea } from '@/components/Textarea';
import { api } from '@/lib/api';
import { buildTenantImpersonationUrl } from '@/lib/tenant-url';

export const IMPERSONATE_REASON_MIN = 5;
const REASON_MAX = 500;

interface ImpersonationTarget {
  id: string;
  displayName: string | null;
  email: string;
  isSuperUser: boolean;
  lastLoginAt: string | null;
}

export interface ImpersonateModalProps {
  tenantId: string;
  tenantName: string;
  onClose: () => void;
}

export function ImpersonateModal({ tenantId, tenantName, onClose }: ImpersonateModalProps) {
  const { t, i18n } = useTranslation();
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const targets = useQuery({
    queryKey: ['admin', 'tenants', tenantId, 'impersonation-targets'],
    queryFn: async () => {
      const res = await api.get<{ users: ImpersonationTarget[] }>(
        `/admin/tenants/${tenantId}/impersonation-targets`,
      );
      return res.data.users;
    },
    // Lista musi być świeża przy każdym otwarciu okna (ktoś mógł być właśnie usunięty/zawieszony).
    staleTime: 0,
    gcTime: 0,
  });

  const users = useMemo(() => targets.data ?? [], [targets.data]);

  // Domyślny wybór: pierwszy superużytkownik (lista przychodzi posortowana), inaczej pierwszy.
  const effectivePicked = picked ?? users[0]?.id ?? null;

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleString(i18n.language === 'en' ? 'en-GB' : 'pl-PL', {
      dateStyle: 'short',
      timeStyle: 'short',
    });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) => u.email.toLowerCase().includes(q) || (u.displayName ?? '').toLowerCase().includes(q),
    );
  }, [users, search]);

  const options = filtered.map((u) => {
    const name = u.displayName?.trim() || t('admin.tenants.actions.impersonate.noName');
    const last = u.lastLoginAt
      ? t('admin.tenants.actions.impersonate.lastLogin', { date: formatDate(u.lastLoginAt) })
      : t('admin.tenants.actions.impersonate.neverLoggedIn');
    return {
      value: u.id,
      label: u.isSuperUser
        ? `${name} · ${t('admin.tenants.actions.impersonate.superUserTag')}`
        : name,
      description: `${u.email} · ${last}`,
    };
  });

  const trimmed = reason.trim();
  const reasonOk = trimmed.length >= IMPERSONATE_REASON_MIN;
  const canSubmit = !submitting && !!effectivePicked && reasonOk;

  const onSubmit = async () => {
    if (!canSubmit || !effectivePicked) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await api.post<{
        sessionKey: string;
        tenantSlug: string;
        tenantId: string;
        expiresAt: string;
      }>(`/admin/tenants/${tenantId}/impersonate`, {
        targetUserId: effectivePicked,
        reason: trimmed,
      });
      // Klucz w URL fragment (`#__impSessionKey=...`), nie query — nie trafia do Referer ani logów
      // dostępu. Cel = shell firmy (`/`), nie `/login`.
      window.open(
        buildTenantImpersonationUrl({
          slug: res.data.tenantSlug,
          tenantId: res.data.tenantId,
          sessionKey: res.data.sessionKey,
        }),
        '_blank',
      );
      onClose();
    } catch (err) {
      const code = (err as AxiosError<{ error?: string }>).response?.data?.error ?? '';
      // Statyczne klucze (bramka check:i18n nie widzi kluczy składanych dynamicznie).
      const messages: Record<string, string> = {
        REASON_REQUIRED: t('admin.tenants.actions.impersonate.errors.REASON_REQUIRED'),
        REASON_TOO_LONG: t('admin.tenants.actions.impersonate.errors.REASON_TOO_LONG'),
        TARGET_USER_NOT_FOUND: t('admin.tenants.actions.impersonate.errors.TARGET_USER_NOT_FOUND'),
        NOT_CARETAKER: t('admin.tenants.actions.impersonate.errors.NOT_CARETAKER'),
        TENANT_UPDATING: t('admin.tenants.dbUpdatingError'),
        NO_ACTIVE_SUBSCRIPTIONS: t(
          'admin.tenants.actions.impersonate.errors.NO_ACTIVE_SUBSCRIPTIONS',
        ),
      };
      setError(messages[code] ?? t('admin.tenants.actions.impersonate.errors.generic'));
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => (submitting ? undefined : onClose())}
      title={t('admin.tenants.actions.impersonate.title', { name: tenantName })}
      description={t('admin.tenants.actions.impersonate.description')}
      closeOnBackdrop={!submitting}
      closeOnEsc={!submitting}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            {t('admin.tenants.actions.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => void onSubmit()}
            loading={submitting}
            disabled={!canSubmit}
            data-testid="impersonate-confirm"
          >
            {submitting
              ? t('admin.tenants.actions.impersonate.submitting')
              : t('admin.tenants.actions.impersonate.confirm')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {targets.isLoading && (
          <div
            className="flex items-center gap-2 text-sm text-text-tertiary"
            data-testid="impersonate-loading"
          >
            <Spinner /> {t('admin.tenants.actions.impersonate.loading')}
          </div>
        )}

        {targets.isError && (
          <div
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
          >
            {t('admin.tenants.actions.impersonate.loadError')}
          </div>
        )}

        {targets.isSuccess && users.length === 0 && (
          <p className="text-sm text-text-tertiary" data-testid="impersonate-empty">
            {t('admin.tenants.actions.impersonate.noUsers')}
          </p>
        )}

        {targets.isSuccess && users.length > 0 && (
          <div className="flex flex-col gap-2">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder={t('admin.tenants.actions.impersonate.searchPlaceholder')}
              data-testid="impersonate-search"
            />
            <div className="max-h-64 overflow-y-auto pr-1">
              {options.length === 0 ? (
                <p className="py-2 text-sm text-text-tertiary">
                  {t('admin.tenants.actions.impersonate.noMatches')}
                </p>
              ) : (
                <RadioGroup
                  name="impersonation-target"
                  legend={t('admin.tenants.actions.impersonate.usersLabel')}
                  value={effectivePicked}
                  onChange={setPicked}
                  options={options}
                />
              )}
            </div>
          </div>
        )}

        <Textarea
          label={t('admin.tenants.actions.impersonate.reasonLabel')}
          hint={t('admin.tenants.actions.impersonate.reasonHint', {
            count: trimmed.length,
          })}
          rows={3}
          maxLength={REASON_MAX}
          required
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={submitting}
          placeholder={t('admin.tenants.actions.impersonate.reasonPlaceholder')}
          data-testid="impersonate-reason"
        />

        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
          {t('admin.tenants.actions.impersonate.warning')}
        </p>

        {error && (
          <div
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
          >
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}
